import { useState, useEffect, useRef } from 'react';
import technicianLocationService from '../services/technicianLocationService';
import { toast } from 'react-toastify';
import {
  FiMapPin,
  FiNavigation,
  FiBatteryCharging,
  FiPlay,
  FiSquare,
  FiPhone,
  FiCheck,
  FiLogOut,
  FiTool,
  FiAlertCircle
} from 'react-icons/fi';
import './TechnicianPortal.css';

const TechnicianPortal = () => {
  const [techAuth, setTechAuth] = useState(() => {
    const saved = localStorage.getItem('tech_auth');
    return saved ? JSON.parse(saved) : null;
  });

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [isOnDuty, setIsOnDuty] = useState(false);
  const [currentCoords, setCurrentCoords] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const watchIdRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Monitor battery if supported by browser
  useEffect(() => {
    if (navigator.getBattery) {
      navigator.getBattery().then((battery) => {
        setBatteryLevel(Math.round(battery.level * 100));
        battery.addEventListener('levelchange', () => {
          setBatteryLevel(Math.round(battery.level * 100));
        });
      });
    }
  }, []);

  // Fetch assigned jobs on login
  useEffect(() => {
    if (techAuth?.token) {
      fetchJobs();
    }
  }, [techAuth]);

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const res = await technicianLocationService.getMyJobs();
      if (res.success) {
        setAssignedJobs(res.data);
      }
    } catch (err) {
      // Non-blocking
    } finally {
      setJobsLoading(false);
    }
  };

  // Handle Technician Login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone || !pin) {
      toast.warning('Please enter phone and 4-digit PIN');
      return;
    }

    try {
      setLoginLoading(true);
      const res = await technicianLocationService.loginTechnician(phone, pin);
      if (res.success) {
        localStorage.setItem('tech_auth', JSON.stringify(res));
        localStorage.setItem('token', res.token); // Set token for interceptor
        setTechAuth(res);
        toast.success(`Welcome back, ${res.data.name}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    stopTracking();
    localStorage.removeItem('tech_auth');
    localStorage.removeItem('token');
    setTechAuth(null);
    toast.info('Logged out');
  };

  // Start continuous GPS tracking
  const startTracking = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your device');
      return;
    }

    setIsOnDuty(true);
    toast.success('On Duty: Live GPS tracking started');

    // Update status to en_route or available
    technicianLocationService.updateStatus('en_route', true).catch(() => {});

    // Watch position
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, speed: rawSpeed, accuracy: acc, heading } = pos.coords;
        setCurrentCoords({ lat: latitude, lng: longitude });
        setAccuracy(Math.round(acc));
        setSpeed(rawSpeed ? Math.round(rawSpeed * 3.6) : 0);

        // Ping backend with location
        technicianLocationService.updateLocation({
          technicianId: techAuth.data._id,
          latitude,
          longitude,
          heading: heading || 0,
          speed: rawSpeed || 0,
          accuracy: acc || 0,
          batteryLevel: batteryLevel ?? 100,
          isTrackingActive: true,
          status: 'en_route',
        }).catch(() => {});
      },
      (err) => {
        toast.error(`GPS Error: ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000,
      }
    );
  };

  // Stop GPS tracking
  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    setIsOnDuty(false);
    technicianLocationService.updateStatus('available', false).catch(() => {});
    toast.info('Off Duty: GPS tracking stopped');
  };

  // Login View
  if (!techAuth) {
    return (
      <div className="tech-portal-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="portal-header-card" style={{ width: '100%', maxWidth: '380px' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'var(--primary-100)',
              color: 'var(--primary-600)',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.75rem',
              marginBottom: '0.75rem'
            }}>
              <FiTool />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--gray-900)' }}>
              Technician Portal
            </h2>
            <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
              Sign in with your phone and PIN to start your shift
            </p>
          </div>

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.375rem' }}>
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: '0.375rem' }}>
                4-Digit PIN (Default: 1234)
              </label>
              <input
                type="password"
                maxLength={6}
                placeholder="PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="portal-action-btn primary"
              style={{ width: '100%', padding: '0.75rem', fontSize: '0.9375rem', marginTop: '0.5rem' }}
            >
              {loginLoading ? 'Signing in...' : 'Sign In & Clock In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Active Portal View
  return (
    <div className="tech-portal-page">
      {/* Header Profile Card */}
      <div className="portal-header-card">
        <div className="portal-top-bar">
          <div className="portal-user-info">
            <h3>{techAuth.data.name}</h3>
            <p>ID: {techAuth.data.employeeId} &bull; {techAuth.data.shop?.name || 'HVAC Service'}</p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--gray-500)',
              cursor: 'pointer',
              fontSize: '1.25rem'
            }}
            title="Log Out"
          >
            <FiLogOut />
          </button>
        </div>

        {/* Duty Toggle Box */}
        <div className={`duty-switch-box ${isOnDuty ? 'active' : ''}`}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--gray-900)' }}>
              {isOnDuty ? 'Tracking Active' : 'Off Duty'}
            </div>
            <div style={{ fontSize: '0.75rem', color: isOnDuty ? '#059669' : 'var(--gray-500)' }}>
              {isOnDuty ? 'Broadcasting live location' : 'Tap start to begin shift'}
            </div>
          </div>

          {isOnDuty ? (
            <button className="duty-toggle-btn btn-stop" onClick={stopTracking}>
              <FiSquare /> End Shift
            </button>
          ) : (
            <button className="duty-toggle-btn btn-start" onClick={startTracking}>
              <FiPlay /> Start Shift
            </button>
          )}
        </div>
      </div>

      {/* Telemetry Status Card */}
      {isOnDuty && (
        <div className="telemetry-card">
          <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--gray-800)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiNavigation style={{ color: 'var(--primary-600)' }} /> Live GPS Status
          </div>
          <div className="telemetry-grid">
            <div className="telemetry-box">
              <div className="title">Accuracy</div>
              <div className="val">{accuracy ? `${accuracy} meters` : '--'}</div>
            </div>
            <div className="telemetry-box">
              <div className="title">Speed</div>
              <div className="val">{speed} km/h</div>
            </div>
            <div className="telemetry-box">
              <div className="title">Battery</div>
              <div className="val" style={{ color: 'var(--success-600)' }}>
                {batteryLevel !== null ? `${batteryLevel}%` : 'Normal'}
              </div>
            </div>
            <div className="telemetry-box">
              <div className="title">Coordinates</div>
              <div className="val" style={{ fontSize: '0.75rem' }}>
                {currentCoords ? `${currentCoords.lat.toFixed(4)}, ${currentCoords.lng.toFixed(4)}` : 'Waiting...'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assigned Jobs */}
      <div className="portal-jobs-title">
        <span>Today's Assigned Jobs ({assignedJobs.length})</span>
        <button
          onClick={fetchJobs}
          style={{ background: 'transparent', border: 'none', color: 'var(--primary-600)', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {assignedJobs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: 'var(--radius-lg)', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
          No active visits assigned to you today.
        </div>
      ) : (
        assignedJobs.map((job) => (
          <div key={job._id} className="portal-job-card">
            <div className="portal-job-card-header">
              <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--gray-900)' }}>
                {job.customer?.name}
              </span>
              <span style={{
                background: job.status === 'in_progress' ? '#fef3c7' : '#dbeafe',
                color: job.status === 'in_progress' ? '#92400e' : '#1e40af',
                fontSize: '0.6875rem',
                fontWeight: 700,
                padding: '0.2rem 0.5rem',
                borderRadius: '9999px',
                textTransform: 'uppercase'
              }}>
                {job.status.replace('_', ' ')}
              </span>
            </div>

            <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.375rem' }}>
              <strong>Issue:</strong> {job.complaint}
            </div>

            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
              <FiMapPin style={{ marginRight: '0.25rem' }} />
              {job.customer?.address?.street}, {job.customer?.address?.city}
            </div>

            <div className="portal-job-actions">
              {job.customer?.phone && (
                <a
                  href={`tel:${job.customer.phone}`}
                  className="portal-action-btn"
                  style={{ textDecoration: 'none' }}
                >
                  <FiPhone /> Call Customer
                </a>
              )}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  `${job.customer?.address?.street || ''} ${job.customer?.address?.city || ''}`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="portal-action-btn primary"
                style={{ textDecoration: 'none' }}
              >
                <FiNavigation /> Navigate
              </a>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default TechnicianPortal;
